import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '../../lib/api';
import { formatCurrency, formatCurrencyFull, formatNumber, formatMonth } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Link } from 'react-router-dom';
import { 
    Music, 
    IndianRupee,
    DollarSign, 
    TrendingUp, 
    FileText, 
    Plus,
    ArrowRight,
    ArrowUpRight,
    Loader2,
    Play,
    Disc3,
    Globe,
    Clock,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Sparkles,
    BarChart3,
    Wallet,
    Hourglass,
    ExternalLink,
    Youtube,
    Instagram,
    Twitter
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import TrackAnalyticsModal from '../../components/TrackAnalyticsModal';
import { useTheme } from '../../context/ThemeContext';
import { compactInr } from '../../lib/chartFormat';

const PLATFORM_COLORS = {
    'Spotify': '#1DB954',
    'Apple Music': '#FA243C',
    'YouTube': '#FF0000',
    'Amazon': '#FF9900',
    'Instagram': '#E4405F',
    'Facebook': '#1877F2',
    'TikTok': '#000000',
    'Tidal': '#000000'
};

const COLORS = ['#7C3AED', '#00FF9D', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3'];

const LabelDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('cash_flow'); // 'performance' or 'cash_flow'
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [trackModalOpen, setTrackModalOpen] = useState(false);
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    // Theme-aware chart colors (fixes #4 light-mode visibility)
    const chartColors = {
        grid:    isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.10)',
        axis:    isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.70)',
        tipBg:   isDark ? '#0f172a' : '#ffffff',
        tipBorder: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.15)',
        tipText: isDark ? '#e5e7eb' : '#0f172a',
        cursor:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const monthType = viewMode === 'performance' ? 'transaction_month' : 'reporting_month';
                const response = await dashboardAPI.getLabel(monthType);
                setData(response.data);
            } catch (error) {
                console.error('Failed to fetch dashboard:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [viewMode]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <div className="text-center space-y-4">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
                    <p className="text-muted-foreground animate-pulse">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    const label = data?.label || {};
    const stats = data?.stats || {};
    const chartData = data?.monthly_revenue?.slice().reverse() || [];
    const topTracks = data?.top_tracks || [];
    const platformData = data?.platform_breakdown || [];
    const recentInvoices = data?.recent_invoices || [];

    // Calculate invoice progress
    const totalInvoices = (stats.pending_invoices || 0) + (stats.approved_invoices || 0) + (stats.paid_invoices || 0) + (stats.rejected_invoices || 0);
    const paidPercentage = totalInvoices > 0 ? ((stats.paid_invoices || 0) / totalInvoices) * 100 : 0;

    return (
        <div className="space-y-8 animate-in fade-in duration-500" data-testid="label-dashboard">
            {/* Premium Hero Section */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-card to-secondary/10 border border-border/40">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }} />
                </div>
                
                {/* Cover Image Overlay (if exists) */}
                {label.cover_image && (
                    <div 
                        className="absolute inset-0 opacity-20 bg-cover bg-center"
                        style={{ backgroundImage: `url(${label.cover_image})` }}
                    />
                )}
                
                <div className="relative p-8 md:p-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            {/* Profile Image/Avatar */}
                            <div className="relative">
                                {label.profile_image ? (
                                    <img 
                                        src={label.profile_image} 
                                        alt={label.name}
                                        className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover border-4 border-primary/20 shadow-xl"
                                    />
                                ) : (
                                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center border-4 border-primary/20 shadow-xl">
                                        <span className="text-4xl md:text-5xl font-heading font-bold text-white">
                                            {label.name?.charAt(0) || 'L'}
                                        </span>
                                    </div>
                                )}
                                <div className="absolute -bottom-2 -right-2 bg-secondary rounded-full p-1.5 shadow-lg">
                                    <CheckCircle2 className="w-5 h-5 text-secondary-foreground" />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">
                                        {label.name || 'Your Label'}
                                    </h1>
                                </div>
                                <p className="text-muted-foreground text-lg">
                                    {label.bio || 'Welcome to your premium music distribution dashboard'}
                                </p>
                                <p className="text-xs text-muted-foreground/80">
                                    {viewMode === 'performance' 
                                        ? 'Performance view • grouped by transaction month (when streams happened)' 
                                        : 'Cash flow view • grouped by reporting month (when platforms paid out)'}
                                </p>
                                
                                {/* Social Links */}
                                <div className="flex items-center gap-3 pt-2">
                                    {label.youtube_url && (
                                        <a href={label.youtube_url} target="_blank" rel="noopener noreferrer" 
                                           className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
                                            <Youtube className="w-5 h-5 text-red-500" />
                                        </a>
                                    )}
                                    {label.instagram_url && (
                                        <a href={label.instagram_url} target="_blank" rel="noopener noreferrer"
                                           className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
                                            <Instagram className="w-5 h-5 text-pink-500" />
                                        </a>
                                    )}
                                    {label.spotify_url && (
                                        <a href={label.spotify_url} target="_blank" rel="noopener noreferrer"
                                           className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
                                            <Disc3 className="w-5 h-5 text-green-500" />
                                        </a>
                                    )}
                                    {label.website && (
                                        <a href={label.website} target="_blank" rel="noopener noreferrer"
                                           className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
                                            <Globe className="w-5 h-5 text-blue-400" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-3">
                            {/* View Mode Toggle */}
                            <div className="flex items-center gap-1 p-1 bg-white/10 backdrop-blur rounded-lg border border-white/10" data-testid="label-view-toggle">
                                <button
                                    onClick={() => setViewMode('performance')}
                                    data-testid="label-toggle-performance"
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                        viewMode === 'performance'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    Performance
                                </button>
                                <button
                                    onClick={() => setViewMode('cash_flow')}
                                    data-testid="label-toggle-cashflow"
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                        viewMode === 'cash_flow'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    Cash Flow
                                </button>
                            </div>
                            <Button asChild variant="outline" className="border-border/50 hover:bg-white/10">
                                <Link to="/label/reports">
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    View Reports
                                </Link>
                            </Button>
                            <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg shadow-secondary/25">
                                <Link to="/label/invoices/create" data-testid="create-invoice-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Invoice
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Financial KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Net Revenue Card */}
                <Card className="relative overflow-hidden bg-gradient-to-br from-secondary/20 to-secondary/5 border-secondary/30 group hover:shadow-lg hover:shadow-secondary/10 transition-all duration-300" data-testid="label-kpi-net-revenue">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform" />
                    <CardContent className="p-6 relative">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 rounded-xl bg-secondary/20">
                                <Wallet className="w-6 h-6 text-secondary" />
                            </div>
                            <Badge variant="outline" className="text-secondary border-secondary/30">
                                <ArrowUpRight className="w-3 h-3 mr-1" />
                                Net
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">Your Net Revenue (INR)</p>
                        <p className="text-3xl font-heading font-bold text-secondary mb-1">
                            {formatCurrency(stats.net_revenue_inr, 'INR')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Gross: {formatCurrency(stats.gross_revenue_inr, 'INR')}
                        </p>
                    </CardContent>
                </Card>

                {/* Pending Amount Card */}
                <Card className="relative overflow-hidden bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 border-yellow-500/30 group hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-300" data-testid="label-kpi-pending-amount">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform" />
                    <CardContent className="p-6 relative">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 rounded-xl bg-yellow-500/20">
                                <Hourglass className="w-6 h-6 text-yellow-500" />
                            </div>
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">Pending Amount</p>
                        <p className="text-3xl font-heading font-bold text-yellow-500 mb-1" data-testid="label-pending-amount-value">
                            {formatCurrency(stats.pending_amount_inr, 'INR')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {formatCurrency(stats.invoiced_pending_inr || 0, 'INR')} invoiced ·{' '}
                            {formatCurrency(stats.unbilled_pending_inr || 0, 'INR')} to invoice
                        </p>
                    </CardContent>
                </Card>

                {/* Streams Card */}
                <Card className="relative overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30 group hover:shadow-lg hover:shadow-primary/10 transition-all duration-300">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform" />
                    <CardContent className="p-6 relative">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 rounded-xl bg-primary/20">
                                <Play className="w-6 h-6 text-primary" />
                            </div>
                            <Badge variant="outline" className="text-primary border-primary/30">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                Plays
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">Total Streams</p>
                        <p className="text-3xl font-heading font-bold text-primary">
                            {formatNumber(stats.total_streams)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Across all platforms
                        </p>
                    </CardContent>
                </Card>

                {/* Catalog Card */}
                <Card className="relative overflow-hidden bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border-cyan-500/30 group hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform" />
                    <CardContent className="p-6 relative">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 rounded-xl bg-cyan-500/20">
                                <Music className="w-6 h-6 text-cyan-500" />
                            </div>
                            <Badge variant="outline" className="text-cyan-500 border-cyan-500/30">
                                <Disc3 className="w-3 h-3 mr-1" />
                                Catalog
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">Total Tracks</p>
                        <p className="text-3xl font-heading font-bold text-cyan-500">
                            {formatNumber(stats.total_songs)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            In your catalog
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Chart - Takes 2 columns */}
                <Card className="lg:col-span-2 bg-card/50 border-border/40">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-secondary" />
                                    Revenue Trend
                                </CardTitle>
                                <CardDescription>
                                    Monthly net revenue ·{' '}
                                    {viewMode === 'performance'
                                        ? 'grouped by transaction month (performance)'
                                        : 'grouped by reporting month (cash flow)'}
                                </CardDescription>
                            </div>
                            <Button variant="ghost" size="sm" asChild>
                                <Link to="/label/revenue" className="text-primary">
                                    View Details <ArrowRight className="w-4 h-4 ml-1" />
                                </Link>
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#00FF9D" stopOpacity={0.4}/>
                                            <stop offset="95%" stopColor="#00FF9D" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis 
                                        dataKey="month" 
                                        tickFormatter={(val) => val ? formatMonth(val) : ''} 
                                        stroke={chartColors.axis}
                                        tick={{ fill: chartColors.axis }}
                                        fontSize={12}
                                    />
                                    <YAxis
                                        stroke={chartColors.axis}
                                        tick={{ fill: chartColors.axis }}
                                        tickFormatter={compactInr}
                                        fontSize={12}
                                    />
                                    <Tooltip 
                                        cursor={{ fill: chartColors.cursor }}
                                        contentStyle={{ 
                                            backgroundColor: chartColors.tipBg, 
                                            border: `1px solid ${chartColors.tipBorder}`,
                                            borderRadius: '12px',
                                            color: chartColors.tipText,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                                        }}
                                        labelStyle={{ color: chartColors.tipText }}
                                        itemStyle={{ color: chartColors.tipText }}
                                        formatter={(value, name, item) => {
                                            const key = item?.dataKey || name;
                                            return [formatCurrencyFull(value, 'INR'), key === 'net_inr' ? 'Net Revenue' : 'Gross Revenue'];
                                        }}
                                        labelFormatter={(label) => formatMonth(label)}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="net_inr" 
                                        name="Net Revenue"
                                        stroke="#00FF9D" 
                                        strokeWidth={3}
                                        fillOpacity={1} 
                                        fill="url(#colorRevenue)" 
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Invoice Status Card */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            Invoice Status
                        </CardTitle>
                        <CardDescription>Your payment overview</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Status Summary */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-yellow-500" />
                                    <span className="text-sm font-medium">Pending</span>
                                </div>
                                <Badge className="bg-yellow-500/20 text-yellow-500">
                                    {stats.pending_invoices || 0}
                                </Badge>
                            </div>
                            
                            <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-blue-500" />
                                    <span className="text-sm font-medium">Approved</span>
                                </div>
                                <Badge className="bg-blue-500/20 text-blue-500">
                                    {stats.approved_invoices || 0}
                                </Badge>
                            </div>
                            
                            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                                <div className="flex items-center gap-3">
                                    <DollarSign className="w-5 h-5 text-secondary" />
                                    <span className="text-sm font-medium">Paid</span>
                                </div>
                                <Badge className="bg-secondary/20 text-secondary">
                                    {stats.paid_invoices || 0}
                                </Badge>
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20" data-testid="label-dashboard-rejected-row">
                                <div className="flex items-center gap-3">
                                    <XCircle className="w-5 h-5 text-destructive" />
                                    <span className="text-sm font-medium">Rejected</span>
                                </div>
                                <Badge className="bg-destructive/20 text-destructive">
                                    {stats.rejected_invoices || 0}
                                </Badge>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {totalInvoices > 0 && (
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Payment Progress</span>
                                    <span>{Math.round(paidPercentage)}% Paid</span>
                                </div>
                                <Progress value={paidPercentage} className="h-2" />
                            </div>
                        )}

                        <Button asChild className="w-full" variant="outline">
                            <Link to="/label/invoices">
                                View All Invoices <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* Bottom Row - Top Tracks & Platforms */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Tracks */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Music className="w-5 h-5 text-secondary" />
                                    Top Performing Tracks
                                </CardTitle>
                                <CardDescription>Your best earning songs</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {topTracks.length > 0 ? topTracks.map((track, index) => (
                                <div 
                                    key={index} 
                                    className="flex items-center gap-4 p-3 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors group cursor-pointer"
                                    onClick={() => {
                                        setSelectedTrack(track.title);
                                        setTrackModalOpen(true);
                                    }}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold shrink-0">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate group-hover:text-primary transition-colors">
                                            {track.title}
                                        </p>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {track.artist} • {formatNumber(track.streams)} streams
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-mono font-bold text-secondary">
                                            {formatCurrency(track.net_inr, 'INR')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">net</p>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Music className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>No track data available yet</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Distribution */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Globe className="w-5 h-5 text-primary" />
                            Platform Distribution
                        </CardTitle>
                        <CardDescription>Revenue by streaming platform</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="h-[200px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={platformData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={70}
                                            paddingAngle={5}
                                            dataKey="gross_inr"
                                            nameKey="platform"
                                        >
                                            {platformData.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-${index}`} 
                                                    fill={PLATFORM_COLORS[entry.platform] || COLORS[index % COLORS.length]} 
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            formatter={(value) => formatCurrencyFull(value, 'INR')}
                                            contentStyle={{ 
                                                backgroundColor: '#0f172a', 
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '8px'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="space-y-2">
                                {platformData.slice(0, 5).map((platform, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/20 transition-colors">
                                        <div className="flex items-center gap-2">
                                            <div 
                                                className="w-3 h-3 rounded-full" 
                                                style={{ backgroundColor: PLATFORM_COLORS[platform.platform] || COLORS[index % COLORS.length] }}
                                            />
                                            <span className="text-sm font-medium truncate">{platform.platform}</span>
                                        </div>
                                        <span className="text-sm font-mono text-muted-foreground">
                                            {formatCurrency(platform.gross_inr, 'INR')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Featured Video Section (if available) */}
            {label.featured_video_url && (
                <Card className="bg-card/50 border-border/40 overflow-hidden">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Play className="w-5 h-5 text-primary" />
                            Featured Content
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="aspect-video rounded-xl overflow-hidden bg-black">
                            <iframe
                                src={label.featured_video_url}
                                title="Featured Video"
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Quick Actions Footer */}
            <div className="flex flex-wrap gap-4 justify-center p-6 rounded-2xl bg-gradient-to-r from-muted/30 via-muted/20 to-muted/30 border border-border/40">
                <Button asChild variant="outline" className="border-border/50">
                    <Link to="/label/revenue">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Revenue Details
                    </Link>
                </Button>
                <Button asChild variant="outline" className="border-border/50">
                    <Link to="/label/reports">
                        <FileText className="w-4 h-4 mr-2" />
                        Full Reports
                    </Link>
                </Button>
                <Button asChild variant="outline" className="border-border/50">
                    <Link to="/label/invoices">
                        <Wallet className="w-4 h-4 mr-2" />
                        All Invoices
                    </Link>
                </Button>
                <Button asChild className="bg-primary hover:bg-primary/90">
                    <Link to="/label/invoices/create">
                        <Plus className="w-4 h-4 mr-2" />
                        New Invoice
                    </Link>
                </Button>
            </div>
            
            {/* Track Analytics Modal */}
            <TrackAnalyticsModal 
                open={trackModalOpen}
                onOpenChange={setTrackModalOpen}
                trackName={selectedTrack}
            />
        </div>
    );
};

export default LabelDashboard;
